import {
  Authenticator,
  ThemeProvider,
  translations,
  useAuthenticator,
} from "@aws-amplify/ui-react"
import type { Theme } from "@aws-amplify/ui-react"
import type { UserDataDto } from "@strtmn/sds/types"
import { Amplify } from "aws-amplify"
import { I18n } from "aws-amplify/utils"
import { useContext, useEffect, useState } from "react"
import {
  Navigate,
  Outlet,
  useLoaderData,
  useLocation,
  useNavigate,
  type LoaderFunction,
} from "react-router-dom"
import { UserNotRegisteredError } from "./api/userData"
import { configureApis } from "./api/utils"
import { CacheKeys } from "./CacheKeys"
import { AppLayoutWrapper, LoadingErrorState, ScrollToTop } from "./components"
import { isProd } from "./config"
import { loadFeatures } from "./config.feature-flags"
import {
  FeatureFlagsProvider,
  reloadUserData,
  UserDataContext,
  UserDataContextHelper,
  UserDataContextProvider,
  UserPreferencesContext,
  UserPreferencesContextProvider,
} from "./contexts"
import { useActiveOrgId, useSignOutHandler } from "./hooks"
import Registration from "./routes/onboarding/Registration"
import amplifyconfig from "./amplifyconfiguration.json"

import "./tw-init.css"
import "@aws-amplify/ui-react/styles.css"
// Supports weights 100-900
import "@fontsource-variable/inter"
import "./global.css"
import "./App.css"

Amplify.configure(amplifyconfig)

configureApis()

I18n.putVocabularies(translations)

const sdsTheme: Theme = {
  name: "sds-theme",
  tokens: {
    // colors: {
    //   font: {
    //     primary: { value: '#008080' },
    //   },
    // },
    components: {
      button: {
        // default style: color, border dark
        _hover: {
          color: "var(--bsl-green)",
          backgroundColor: "var(--green-lighter-amp10)",
          borderColor: "var(--bsl-green)",
        },
        _focus: {
          color: "{components.button._hover.color}",
          backgroundColor: "{components.button._hover.backgroundColor}",
          borderColor: "{components.button._hover.borderColor}",
        },
        _active: {
          color: "{components.button._hover.color}",
          backgroundColor: "var(--green-lighter-amp20)",
          borderColor: "{components.button._hover.borderColor}",
        },
        primary: {
          color: "white",
          backgroundColor: "var(--bsl-green)",
          borderColor: "var(--bsl-green)",
          _hover: {
            color: "{components.button.primary.color}",
            backgroundColor: "var(--bsl-green-darker-10)",
            borderColor: "{components.button.primary._hover.backgroundColor}",
          },
          _focus: {
            color: "{components.button.primary._hover.color}",
            backgroundColor:
              "{components.button.primary._hover.backgroundColor}",
            borderColor: "{components.button.primary._focus.backgroundColor}",
          },
          _active: {
            backgroundColor: "var(--bsl-green-darker-20)",
            borderColor: "{components.button.primary._active.backgroundColor}",
          },
        },
        link: {
          color: "var(--bsl-green)",
          _hover: {
            color: "{components.button.link.color}",
            backgroundColor: "{components.button._hover.backgroundColor}",
          },
          _focus: {
            color: "{components.button.link.color}",
            backgroundColor: "{components.button._hover.backgroundColor}",
          },
          _active: {
            color: "{components.button.link.color}",
            backgroundColor: "{components.button._active.backgroundColor}",
            borderColor: "{components.button._active.backgroundColor}",
          },
        },
      },
      fieldcontrol: {
        _focus: {
          borderColor: "var(--bsl-green)",
          boxShadow: "0px 0px 0px 2px var(--bsl-green)", // Dimensions are Amplify UI defaults
        },
      },
      tabs: {
        item: {
          _hover: {
            color: "var(--bsl-green)",
          },
          _focus: {
            color: "var(--bsl-green)",
          },
          _active: {
            color: "var(--bsl-green)",
            borderColor: "var(--bsl-green)",
            backgroundColor: "var(--green-lighter-amp10)",
          },
        },
      },
    },
    fonts: {
      default: {
        static:
          '"Inter", -apple-system, BlinkMacSystemFont, "Helvetica Neue", "Segoe UI", Oxygen, Ubuntu, Cantarell, "Open Sans", sans-serif',
        variable:
          '"Inter Variable", "InterVariable", "Inter var", "Inter", -apple-system, BlinkMacSystemFont, "Helvetica Neue", "Segoe UI", Oxygen, Ubuntu, Cantarell, "Open Sans", sans-serif',
      },
    },
    // fontSizes: {
    //   medium: "12px",
    //   large: "14px",
    //   xl: "16px",
    // },
  },
}

/**
 *  @note: `AppAuthSwitcher` must be its own component
 * (vs. inlined in the `App` function below) to be able to use the `useAuthenticator` hook.
 */
function AppAuthSwitcher() {
  const location = useLocation()
  const { authStatus } = useAuthenticator((context) => [context.authStatus])

  if (authStatus === "configuring") {
    return (
      <LoadingErrorState
        isLoading={true}
        loadingMessage={
          isProd
            ? "Lade Daten ..."
            : 'Loading (authStatus === "configuring") ...'
        }
        isError={false}
        errorMessage=""
      />
    )
  }

  if (location.pathname.startsWith("/login")) {
    // console.log("🔑 AppAuthSwitcher: Rendering public login route")
    return <Outlet />
  }

  if (authStatus !== "authenticated") {
    // console.log(`🔑 AppAuthSwitcher: Redirect to login (authStatus === ${authStatus})`)
    return <Navigate to={`/login`} state={{ from: location }} replace />
  } else {
    // console.log("🔑 AppAuthSwitcher: Rendering private route")
    return (
      <ScrollToTop>
        <UserDataContextProvider>
          <UserPreferencesContextProvider>
            <AppAuthenticated />
          </UserPreferencesContextProvider>
        </UserDataContextProvider>
      </ScrollToTop>
    )
  }
}

function AppAuthenticated() {
  const navigate = useNavigate()
  const { authStatus } = useAuthenticator((context) => [context.authStatus])
  const loaderData = useLoaderData() as LoaderData
  const isNotRegistered = isNotRegisteredMarker(loaderData)
  const userDataFromLoader = isNotRegistered ? null : loaderData
  // console.log("AppAuthenticated: loader user data =", userDataFromLoader)
  const { userPreferences, setUserPreference } = useContext(
    UserPreferencesContext
  )
  // console.log("AppAuthenticated: userPreferences =", userPreferences)
  const {
    userData: userDataCTX,
    setUserData,
    clearUserData,
  } = useContext(UserDataContext)
  console.log("AppAuthenticated: userDataCTX =", userDataCTX)
  const signOutHandler = useSignOutHandler(clearUserData)

  const [activeOrgId, setActiveOrgId] = useState<string | undefined>(
    useActiveOrgId()
  )
  // console.log("AppAuthenticated: activeOrgId =", activeOrgId)
  const [reloadRequired, setReloadRequired] = useState(false)

  if (
    authStatus === "authenticated" &&
    !userDataFromLoader &&
    !isNotRegistered
  ) {
    if (!reloadRequired) {
      // console.log("AppAuthenticated.useEffect (do-navigate) -> really set should reload")
      setReloadRequired(true)
    }
  }

  useEffect(() => {
    // console.log("App.useEffect (user data -> context)")
    if (userDataFromLoader && !userDataCTX) {
      // console.log("Got user data, updating CONTEXT")
      if (!setUserData) {
        console.warn("❌ setUserData is not set.")
      } else {
        setUserData(userDataFromLoader)
      }
    }
  }, [setUserData, userDataFromLoader, userDataCTX])

  useEffect(() => {
    // console.log("AppAuthenticated.useEffect (do-navigate): authStatus =", authStatus)
    if (reloadRequired) {
      // console.log("AppAuthenticated.useEffect (do-navigate) -> should reload")
      setReloadRequired(false)
      setTimeout(() => {
        navigate(".", { state: null, replace: true })
      }, 100)
    }
  }, [authStatus, navigate, reloadRequired])

  useEffect(() => {
    // console.log("> AppAuthenticated.useEffect (getActiveOrgId)")
    if (!userPreferences) {
      // console.log("AppAuthenticated.useEffect (getActiveOrgId): skip, userPref not loaded, yet")
      return
    }
    const _activeOrgId = userPreferences[CacheKeys.UserPreferences.ActiveOrgId] // cannot use hook (useActiveOrgId) inside callback
    // console.log("_activeOrgId:", _activeOrgId)
    if (_activeOrgId) {
      console.log("Active org id found in userPreferences:", _activeOrgId)
      setActiveOrgId(_activeOrgId)
    } else {
      if (!userDataCTX) {
        console.log("❌ Cannot select an active org: no userData in ctx.")
      } else {
        const firstOrg = UserDataContextHelper.findOrgs(userDataCTX)[0]!
        if (!firstOrg) {
          console.log("❌ Cannot set active org: userData has no orgs.")
        } else {
          console.log("⚡ No active org found, selecting the first one.")
          // console.log("firstOrg:", firstOrg.id)
          // console.log("calling setActiveOrgId")
          setActiveOrgId(firstOrg.id)
          // console.log("calling setUserPreference")
          if (!setUserPreference) {
            console.log("❌ setUserPreference is not set.")
          } else {
            setUserPreference(
              CacheKeys.UserPreferences.ActiveOrgId,
              firstOrg.id
            )
          }
        }
      }
    }
  }, [setUserPreference, userPreferences, userDataCTX])

  if (isNotRegistered && !userDataCTX) {
    return (
      <AppLayoutWrapper
        userData={undefined}
        activeOrgId={undefined}
        signOutHandler={signOutHandler}
      >
        <Registration />
      </AppLayoutWrapper>
    )
  }
  if (!userDataCTX) {
    return (
      <AppLayoutWrapper
        userData={userDataCTX}
        activeOrgId={activeOrgId}
        signOutHandler={signOutHandler}
      >
        <LoadingErrorState
          isLoading={true}
          loadingMessage={
            isProd ? "Lade Daten ..." : "Loading (userDataCTX) ..."
          }
          isError={false}
          errorMessage=""
        />
      </AppLayoutWrapper>
    )
  }
  return (
    <AppLayoutWrapper
      userData={userDataCTX}
      activeOrgId={activeOrgId}
      signOutHandler={signOutHandler}
    >
      <Outlet />
    </AppLayoutWrapper>
  )
}

export default function App() {
  return (
    <ThemeProvider theme={sdsTheme}>
      <Authenticator.Provider>
        <FeatureFlagsProvider loadFeatures={loadFeatures}>
          <AppAuthSwitcher />
        </FeatureFlagsProvider>
      </Authenticator.Provider>
    </ThemeProvider>
  )
}

/** Loader result for a signed-in Cognito user who has not registered yet. */
type NotRegisteredMarker = { notRegistered: true }
type LoaderData = UserDataDto | NotRegisteredMarker | null

const isNotRegisteredMarker = (data: LoaderData): data is NotRegisteredMarker =>
  !!data && "notRegistered" in data && data.notRegistered === true

export const loader: LoaderFunction = async (): Promise<LoaderData> => {
  console.log("> App.loader")
  // const { authStatus } = useAuthenticator((context) => [context.authStatus])
  // !isProd && console.log("App.loader: authStatus =", authStatus)
  /**
   * @todo: sometimes the app hangs after Login without userDataCtx.
   * This reload uses cache. Maybe disable cache here (ignoreCache flag)?
   */
  let userData: UserDataDto | undefined
  try {
    userData = await reloadUserData()
  } catch (err) {
    if (err instanceof UserNotRegisteredError) {
      console.log("App.loader: user not registered yet")
      return { notRegistered: true }
    }
    throw err
  }
  if (!userData) {
    console.log("❌ App.loader: got no userData")
    // !isProd && console.log("App.loader: authStatus =", authStatus)
    /**
     * @todo: sometimes the app hangs after Login without userDataCtx.
     * Then a reload could be made after some waiting time.
     */
    return null
  }
  console.log("✅ App.loader: got userData")
  return userData
}

export const Component = App
